import { useCallback, useRef, useState } from 'react';
import {
    useAddTagMutation,
    useAddToFlowMutation,
    useDeleteCustomersMutation,
    useGrantAffiliateMutation,
    useGrantProductMutation,
    useRemoveTagMutation,
} from '../queries/useBulkCustomerMutations';

const DEFAULT_BATCH_SIZE = 500;

interface UseBulkCustomerActionsProps {
    filterArray: string | undefined;
    totalCustomers: number;
    onSuccess?: (result?: { totalDeleted?: number; totalCount?: number }) => void;
    onError?: (error: Error) => void;
}

interface BatchProcessingState {
    isProcessing: boolean;
    processedCount: number;
    totalCount: number;
    userFanIds: number[];
}

export function useBulkCustomerActions({
    filterArray,
    totalCustomers,
    onSuccess,
    onError,
}: UseBulkCustomerActionsProps) {
    const [batchState, setBatchState] = useState<BatchProcessingState>({
        isProcessing: false,
        processedCount: 0,
        totalCount: 0,
        userFanIds: [],
    });

    // Use a ref to always have the latest filterArray available in mutations
    const filterArrayRef = useRef(filterArray || '[]');
    filterArrayRef.current = filterArray || '[]';

    const resetState = useCallback(() => {
        setBatchState({
            isProcessing: false,
            processedCount: 0,
            totalCount: 0,
            userFanIds: [],
        });
    }, []);

    // Generic batch processor
    const processBatch = useCallback(
        async <T>(
            userFanIds: number[],
            batchFn: (batch: number[]) => Promise<T>,
            batchSize: number = DEFAULT_BATCH_SIZE
        ): Promise<boolean> => {
            let processedCount = 0;
            const totalCount = userFanIds.length;

            setBatchState({
                isProcessing: true,
                processedCount: 0,
                totalCount,
                userFanIds,
            });

            while (processedCount < totalCount) {
                const batch = userFanIds.slice(processedCount, processedCount + batchSize);
                await batchFn(batch);
                processedCount = Math.min(totalCount, processedCount + batchSize);
                setBatchState(prev => ({ ...prev, processedCount }));
            }
            return true;
        },
        []
    );

    // Use a getter function to always get the latest filterArray
    const getFilterArray = useCallback(() => filterArrayRef.current, []);

    const mutationOptions = {
        getFilterArray,
        processBatch,
        onSuccess,
        onError,
        resetState,
    };

    const addTagMutation = useAddTagMutation(mutationOptions);
    const removeTagMutation = useRemoveTagMutation(mutationOptions);
    const grantProductMutation = useGrantProductMutation(mutationOptions);
    const grantAffiliateMutation = useGrantAffiliateMutation(mutationOptions);
    const addToFlowMutation = useAddToFlowMutation(mutationOptions);
    const deleteCustomersMutation = useDeleteCustomersMutation(mutationOptions);

    const isLoading =
        addTagMutation.isPending ||
        removeTagMutation.isPending ||
        grantProductMutation.isPending ||
        grantAffiliateMutation.isPending ||
        addToFlowMutation.isPending ||
        deleteCustomersMutation.isPending;

    return {
        // State
        batchState,
        isLoading,
        totalCustomers,

        // Actions
        addTag: (tag: string) => addTagMutation.mutate({ tag }),
        removeTag: (tag: string) => removeTagMutation.mutate({ tag }),
        grantProductAccess: (pageId: number) => grantProductMutation.mutate({ pageId }),
        grantAffiliateAccess: (pageId: number) => grantAffiliateMutation.mutate({ pageId }),
        addToEmailFlow: (flowId: number) => addToFlowMutation.mutate({ flowId }),
        deleteCustomers: () => deleteCustomersMutation.mutate(),
        resetState,
    };
}
